import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import { aggregateDatasetsFromSubmissions, aggregateDatasetsByCategory, Submission } from '../utils/firestore';
import './DatasetDetail.css';

interface DatasetDetailProps {
  user: any;
}

export default function DatasetDetail({ user }: DatasetDetailProps) {
  const { colors } = useTheme();
  const { datasetId } = useParams<{ datasetId: string }>();
  const navigate = useNavigate();
  const [dataset, setDataset] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'tasks' | 'labels'>('tasks');

  useEffect(() => {
    if (!datasetId) {
      navigate('/marketplace');
      return;
    }

    const loadDataset = async () => {
      try {
        setLoading(true);

        // Parse datasetId
        // New format: just category name (e.g., "Handwriting")
        // Old format: category_subcategory (e.g., "Handwriting_Letters") - for backward compatibility
        const hasUnderscore = datasetId.includes('_');
        let category: string;
        let subcategory: string | null = null;
        
        if (hasUnderscore) {
          // Old format: category_subcategory
          const parts = datasetId.split('_');
          category = parts[0];
          subcategory = parts.slice(1).join('_'); // Handle subcategories with underscores
        } else {
          // New format: category only
          category = datasetId;
        }
        
        // Query all completed submissions that match this category (and subcategory if specified)
        const submissionsRef = collection(db, 'submissions');
        const submissionsSnapshot = await getDocs(submissionsRef);
        
        const allSubmissions: Submission[] = [];
        submissionsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          // Filter for completed status and matching category
          if (data.status === 'completed' || data.status === 'approved') {
            const subCategory = data.category || data.taskCategory || '';
            const subSubcategory = data.subcategory || data.taskSubcategory || '';
            
            // Match category, and subcategory only if specified (old format)
            const categoryMatches = subCategory === category;
            const subcategoryMatches = subcategory === null || subSubcategory === subcategory;
            
            if (categoryMatches && subcategoryMatches) {
              // Calculate label count from labels array if labelCount is not available
              const labelsArray = data.labels || [];
              const calculatedLabelCount = data.labelCount !== undefined && data.labelCount !== null
                ? data.labelCount
                : (Array.isArray(labelsArray) 
                    ? labelsArray.filter((label: any) => label !== null && label !== undefined).length 
                    : 0);
              
              // Destructure to exclude labelCount and labels from spread, then add them back
              const { labelCount: _, labels: __, ...dataWithoutLabels } = data;
              
              allSubmissions.push({
                ...dataWithoutLabels,
                id: doc.id,
                submissionId: data.submissionId || doc.id,
                userId: data.userId || '',
                taskId: data.taskId || '',
                taskTitle: data.taskTitle || data.taskName || data.task?.title,
                category: subCategory,
                subcategory: subSubcategory,
                mediaUrl: data.mediaUrl || data.assetUrl || data.imageUrl,
                assetUrl: data.assetUrl || data.mediaUrl || data.imageUrl,
                labels: labelsArray,
                labelCount: calculatedLabelCount,
                createdAt: data.createdAt,
                approvedAt: data.approvedAt,
                completedAt: data.completedAt || data.approvedAt
              });
            }
          }
        });

        if (allSubmissions.length === 0) {
          navigate('/marketplace');
          return;
        }

        // Aggregate into dataset
        // If category-only (new format), use category aggregation to get one dataset
        // If category_subcategory (old format), use subcategory aggregation
        const datasets = subcategory === null 
          ? aggregateDatasetsByCategory(allSubmissions)
          : aggregateDatasetsFromSubmissions(allSubmissions);
        const dataset = datasets[0];

        // Load task descriptions from Firestore
        const taskDescriptionsMap = new Map<string, string>();
        try {
          const uniqueTaskIds = Array.from(new Set(allSubmissions.map((sub: any) => sub.taskId || sub.taskID || sub.task?.id).filter(Boolean)));
          for (const taskId of uniqueTaskIds) {
            try {
              const taskDoc = await getDoc(doc(db, 'tasks', taskId));
              if (taskDoc.exists()) {
                const taskData = taskDoc.data();
                taskDescriptionsMap.set(taskId, taskData.description || '');
              }
            } catch (err) {
              console.warn(`Failed to load task ${taskId}:`, err);
            }
          }
        } catch (err) {
          console.warn('Failed to load task descriptions', err);
        }

        // Get task stats with description
        const taskStatsMap = new Map<string, { 
          taskTitle: string; 
          subcategory: string;
          description: string;
          images: number; 
          labels: number;
        }>();
        allSubmissions.forEach((sub: any) => {
          const taskId = sub.taskId || sub.taskID || sub.task?.id || 'unknown';
          const taskTitle = sub.taskTitle || sub.taskName || sub.task?.title || 'Unknown Task';
          const subcategory = sub.subcategory || sub.taskSubcategory || 'N/A';
          const description = taskDescriptionsMap.get(taskId) || '';
          const labelCount = sub.labelCount || (Array.isArray(sub.labels) ? sub.labels.filter((l: any) => l !== null && l !== undefined).length : 0);
          
          if (!taskStatsMap.has(taskId)) {
            taskStatsMap.set(taskId, { taskTitle, subcategory, description, images: 0, labels: 0 });
          }
          const stats = taskStatsMap.get(taskId)!;
          stats.images += 1;
          stats.labels += labelCount;
        });
        const taskStats = Array.from(taskStatsMap.values()).sort((a, b) => b.images - a.images);
        const taskTitles = Array.from(new Set(taskStats.map(t => t.taskTitle))).sort();

        // Get sample images with task names
        const sampleImagesWithTasks = allSubmissions
          .map((sub: any) => ({
            url: sub.mediaUrl || sub.assetUrl,
            taskTitle: sub.taskTitle || sub.taskName || sub.task?.title || 'Unknown'
          }))
          .filter((img: any) => img.url)
          .slice(0, 12);

        // Load label schemas (for schema class objects)
        // Schema classes have IDs like "class_1759865187935" and are stored in labelSchemas/{schemaId}.classes[]
        const schemaClassMap = new Map<string, { 
          displayName: string;
          objectId: string; // schema class ID (e.g., "class_1759865187935")
          objectName: string; // schema class name
          description?: string;
          schemaId: string;
          schemaName: string;
          category?: string;
          shapes?: string[];
          source: 'schema';
        }>();
        
        try {
          const schemasSnapshot = await getDocs(collection(db, 'labelSchemas'));
          schemasSnapshot.docs.forEach((schemaDoc) => {
            const schemaData: any = schemaDoc.data();
            const schemaId = schemaDoc.id;
            const schemaName = schemaData.name || schemaId;
            const classes = schemaData.classes || [];
            
            classes.forEach((cls: any) => {
              const classId = cls.id;
              if (classId) {
                schemaClassMap.set(classId, {
                  displayName: cls.displayName || cls.name || classId,
                  objectId: classId,
                  objectName: cls.name || classId,
                  description: cls.description || '',
                  schemaId: schemaId,
                  schemaName: schemaName,
                  category: cls.category || '',
                  shapes: cls.shapes || [],
                  source: 'schema'
                });
              }
            });
          });
          console.log('Loaded schema classes:', schemaClassMap.size);
        } catch (err) {
          console.warn('Failed to load label schemas', err);
        }
        
        // Load global categories (for global objects)
        // Global objects have document IDs that match categoryId in labels
        const globalCategoriesMap = new Map<string, { 
          displayName: string; 
          objectId: string; // document ID
          objectName: string; // name field
          description?: string;
          category?: string;
          categoryDisplayName?: string;
          color?: string;
          shapes?: string[];
          source: 'global';
        }>();
        
        try {
          const globalCategoriesSnapshot = await getDocs(collection(db, 'globalCategories'));
          globalCategoriesSnapshot.docs.forEach((doc) => {
            const data: any = doc.data();
            globalCategoriesMap.set(doc.id, {
              displayName: data.displayName || data.name || doc.id,
              objectId: doc.id,
              objectName: data.name || doc.id,
              description: data.description || '',
              category: data.category || '',
              color: data.color || '',
              shapes: data.shapes || [],
              source: 'global' as const
            });
          });
          console.log('Loaded global categories:', globalCategoriesMap.size);
        } catch (err) {
          console.warn('Failed to load global categories', err);
        }
        
        // Load object categories for category display names
        const objectCategoriesMap = new Map<string, string>();
        try {
          const objectCategoriesSnapshot = await getDocs(collection(db, 'objectCategories'));
          objectCategoriesSnapshot.docs.forEach((doc) => {
            const data: any = doc.data();
            objectCategoriesMap.set(doc.id, data.displayName || data.name || doc.id);
          });
        } catch (err) {
          console.warn('Failed to load object categories', err);
        }
        
        // Update category display names
        globalCategoriesMap.forEach((info, id) => {
          if (info.category && objectCategoriesMap.has(info.category)) {
            info.categoryDisplayName = objectCategoriesMap.get(info.category)!;
          }
        });
        
        // Debug: Log a sample of global categories to verify data
        if (globalCategoriesMap.size > 0) {
          const sampleEntry = Array.from(globalCategoriesMap.entries())[0];
          console.log('Sample global category entry:', {
            id: sampleEntry[0],
            data: sampleEntry[1]
          });
        }

        // Function to get object info from categoryId
        // categoryId can be either:
        // 1. A schema class ID (e.g., "class_1759865187935") - check schemaClassMap first
        // 2. A global category document ID - check globalCategoriesMap second
        // This matches the logic in Review.tsx getCategoryName()
        const getObjectInfo = (categoryId: string): {
          displayName: string;
          objectId: string;
          objectName: string;
          description?: string;
          category?: string;
          categoryDisplayName?: string;
          shapes?: string[];
          color?: string;
          source: 'schema' | 'global';
        } | null => {
          if (!categoryId) return null;
          
          // Priority 1: Check schema classes first (matches Review.tsx logic)
          if (schemaClassMap.has(categoryId)) {
            const schemaObj = schemaClassMap.get(categoryId)!;
            // Look up category display name if category exists
            const categoryDisplayName = schemaObj.category && objectCategoriesMap.has(schemaObj.category)
              ? objectCategoriesMap.get(schemaObj.category)!
              : undefined;
            return {
              displayName: schemaObj.displayName,
              objectId: schemaObj.objectId,
              objectName: schemaObj.objectName,
              description: schemaObj.description,
              category: schemaObj.category,
              categoryDisplayName,
              shapes: schemaObj.shapes,
              source: 'schema'
            };
          }
          
          // Priority 2: Check global categories
          if (globalCategoriesMap.has(categoryId)) {
            const globalObj = globalCategoriesMap.get(categoryId)!;
            return {
              displayName: globalObj.displayName,
              objectId: globalObj.objectId,
              objectName: globalObj.objectName,
              description: globalObj.description,
              category: globalObj.category,
              categoryDisplayName: globalObj.categoryDisplayName,
              shapes: globalObj.shapes,
              color: globalObj.color,
              source: 'global'
            };
          }
          
          return null;
        };

        // Count labels by object name - simple aggregation
        const labelBreakdownMap = new Map<string, { 
          count: number; 
          objectName: string;
          objectId: string;
          category?: string;
          categoryDisplayName?: string;
          shapes?: string[];
          color?: string;
        }>();
        
        // Track missing categoryIds for debugging
        const missingCategoryIds = new Set<string>();
        
        // Process all labels and objects
        allSubmissions.forEach((sub: any) => {
          // Check labels array
          // Labels have structure: { categoryId: string; bbox: [number, number, number, number]; attributes?: any }
          const labels = sub.labels || [];
          if (Array.isArray(labels)) {
            labels.forEach((label: any) => {
              // ONLY use categoryId - this is the document ID in globalCategories
              // Do NOT fall back to classId, class, className, or id - those are not global category IDs
              const categoryId = label.categoryId;
              if (!categoryId) return; // Skip labels without categoryId
              
              const objectInfo = getObjectInfo(categoryId);
              
              if (!objectInfo) {
                // Track missing IDs for debugging
                missingCategoryIds.add(categoryId);
                return; // Skip labels that don't have a matching global category
              }
              
              // Use object displayName as the key for aggregation
              const objectKey = objectInfo.displayName;
              
              const existing = labelBreakdownMap.get(objectKey) || {
                count: 0,
                objectName: objectInfo.displayName,
                objectId: objectInfo.objectId,
                category: objectInfo.category || '',
                categoryDisplayName: objectInfo.categoryDisplayName || '',
                shapes: objectInfo.shapes || [],
                color: objectInfo.color || ''
              };
              
              existing.count += 1;
              labelBreakdownMap.set(objectKey, existing);
            });
          }
          
          // Check objects array (if it exists)
          const objects = sub.objects || [];
          if (Array.isArray(objects)) {
            objects.forEach((obj: any) => {
              // ONLY use categoryId - same as labels
              const categoryId = obj.categoryId;
              if (!categoryId) return;
              
              const objectInfo = getObjectInfo(categoryId);
              
              if (!objectInfo) {
                missingCategoryIds.add(categoryId);
                return;
              }
              
              const objectKey = objectInfo.displayName;
              
              const existing = labelBreakdownMap.get(objectKey) || {
                count: 0,
                objectName: objectInfo.displayName,
                objectId: objectInfo.objectId,
                description: objectInfo.description || '',
                category: objectInfo.category || '',
                categoryDisplayName: objectInfo.categoryDisplayName || '',
                shapes: objectInfo.shapes || [],
                color: objectInfo.color || ''
              };
              
              existing.count += 1;
              labelBreakdownMap.set(objectKey, existing);
            });
          }
        });
        
        // Log missing categoryIds for debugging
        if (missingCategoryIds.size > 0) {
          console.warn('Labels with categoryIds not found in globalCategories:', Array.from(missingCategoryIds));
        }

        // Create label breakdown - sorted by count
        const labelBreakdown = Array.from(labelBreakdownMap.values())
          .sort((a, b) => b.count - a.count);

        // Calculate total labels directly from submissions as a fallback
        // This ensures we get the correct count even if aggregation had issues
        const calculatedTotalLabels = allSubmissions.reduce((sum, sub) => {
          const labelCount = sub.labelCount !== undefined && sub.labelCount !== null
            ? sub.labelCount
            : (Array.isArray(sub.labels) 
                ? sub.labels.filter((label: any) => label !== null && label !== undefined).length 
                : 0);
          return sum + labelCount;
        }, 0);

        // Use calculated total if dataset.totalLabels is 0 or missing
        const finalTotalLabels = dataset.totalLabels > 0 ? dataset.totalLabels : calculatedTotalLabels;

        console.log('Dataset label count debug:', {
          datasetTotalLabels: dataset.totalLabels,
          calculatedTotalLabels,
          finalTotalLabels,
          submissionCount: allSubmissions.length,
          sampleSubmission: allSubmissions[0] ? {
            labelCount: allSubmissions[0].labelCount,
            labelsArrayLength: Array.isArray(allSubmissions[0].labels) ? allSubmissions[0].labels.length : 0
          } : null
        });

        setDataset({
          id: dataset.datasetId,
          name: subcategory === null 
            ? `${dataset.category} - Complete Set`
            : `${dataset.category} - ${dataset.subcategory}`,
          category: dataset.category,
          subcategory: dataset.subcategory,
          numImages: dataset.totalImages,
          numLabels: finalTotalLabels,
          labelTypes: ['bbox', 'classification'],
          sampleImages: sampleImagesWithTasks,
          pricePIX: finalTotalLabels * 10,
          tasksIncluded: taskTitles,
          taskStats,
          labelBreakdown,
          submissions: allSubmissions,
        });
      } catch (error) {
        console.error('Failed to load dataset', error);
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };

    loadDataset();
  }, [datasetId, navigate]);

  const handlePurchase = async () => {
    if (!datasetId || !dataset) return;
    alert('Purchase functionality coming soon!');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: colors.text }}>
        Loading dataset...
      </div>
    );
  }

  if (!dataset) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: colors.text }}>
        Dataset not found
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/marketplace')}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: `1px solid ${colors.border}`,
            color: colors.text,
            borderRadius: '6px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Marketplace
        </button>
        <h1 style={{ margin: '0 0 10px 0', color: colors.text, fontSize: '28px' }}>
          {dataset.name}
        </h1>
        <p style={{ color: colors.secondary, margin: 0 }}>
          {dataset.category} • {dataset.subcategory}
        </p>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div className="detail-card" style={{ background: colors.card, borderColor: colors.border }}>
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>TOTAL IMAGES</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: colors.primary }}>
            {dataset.numImages.toLocaleString()}
          </div>
        </div>
        <div className="detail-card" style={{ background: colors.card, borderColor: colors.border }}>
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>TOTAL LABELS</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: colors.primary }}>
            {dataset.numLabels.toLocaleString()}
          </div>
        </div>
        <div className="detail-card" style={{ background: colors.card, borderColor: colors.border }}>
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>CATEGORY</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: colors.primary }}>
            {dataset.category}
          </div>
        </div>
      </div>

      {(dataset.taskStats && dataset.taskStats.length > 0) || (dataset.labelBreakdown && dataset.labelBreakdown.length > 0) ? (
        <div className="detail-card" style={{ background: colors.card, borderColor: colors.border }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('tasks')}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'tasks' ? `2px solid ${colors.primary}` : '2px solid transparent',
                color: activeTab === 'tasks' ? colors.primary : colors.text,
                cursor: 'pointer',
                fontWeight: activeTab === 'tasks' ? '600' : '400',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              Tasks Breakdown
            </button>
            <button
              onClick={() => setActiveTab('labels')}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'labels' ? `2px solid ${colors.primary}` : '2px solid transparent',
                color: activeTab === 'labels' ? colors.primary : colors.text,
                cursor: 'pointer',
                fontWeight: activeTab === 'labels' ? '600' : '400',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              Labels Breakdown
            </button>
          </div>

          {activeTab === 'tasks' && dataset.taskStats && dataset.taskStats.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ 
                    background: colors.background,
                    borderBottom: `2px solid ${colors.border}`
                  }}>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Task Name
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Subcategory
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Description
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'right',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Images
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'right',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Labels
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dataset.taskStats.map((task: any, idx: number) => (
                    <tr 
                      key={idx}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        background: colors.card
                      }}
                    >
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        fontWeight: '500'
                      }}>
                        {task.taskTitle}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        color: colors.text
                      }}>
                        {task.subcategory && task.subcategory !== 'N/A' ? task.subcategory : '-'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        color: colors.text,
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={task.description || ''}
                      >
                        {task.description ? (task.description.length > 50 ? task.description.substring(0, 50) + '..' : task.description) : ''}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        textAlign: 'right',
                        borderColor: colors.border,
                        color: colors.text
                      }}>
                        {task.images.toLocaleString()}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        textAlign: 'right',
                        borderColor: colors.border,
                        color: colors.text
                      }}>
                        {task.labels.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'labels' && dataset.labelBreakdown && dataset.labelBreakdown.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ 
                    background: colors.background,
                    borderBottom: `2px solid ${colors.border}`
                  }}>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Object
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Object ID
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Description
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Category
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Shapes
                    </th>
                    <th style={{ 
                      padding: '12px 16px',
                      textAlign: 'right',
                      borderColor: colors.border,
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}>
                      Qty
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dataset.labelBreakdown.map((label: any, idx: number) => (
                    <tr 
                      key={idx}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        background: colors.card
                      }}
                    >
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        fontWeight: '500'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {label.color && (
                            <span 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                borderRadius: '50%', 
                                backgroundColor: label.color,
                                display: 'inline-block',
                                border: `2px solid ${colors.border || '#e5e7eb'}`
                              }} 
                            />
                          )}
                          <span>{label.objectName}</span>
                        </div>
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        color: colors.secondary,
                        fontSize: '13px',
                        fontFamily: 'monospace'
                      }}>
                        {label.objectId || '-'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        color: colors.text,
                        fontSize: '13px'
                      }}>
                        {label.description || ''}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        color: colors.text,
                        fontSize: '13px'
                      }}>
                        {label.categoryDisplayName || label.category || '-'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        borderColor: colors.border,
                        color: colors.text,
                        fontSize: '13px'
                      }}>
                        {label.shapes && label.shapes.length > 0 ? label.shapes.join(', ') : 'rectangle'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px',
                        textAlign: 'right',
                        borderColor: colors.border,
                        color: colors.text,
                        fontWeight: '500'
                      }}>
                        {label.count ? label.count.toLocaleString() : '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {dataset.sampleImages && dataset.sampleImages.length > 0 && (
        <div className="detail-card" style={{ background: colors.card, borderColor: colors.border, marginTop: '30px' }}>
          <h3 style={{ margin: '0 0 20px 0', color: colors.text }}>Sample Images</h3>
          <div className="sample-grid-12">
            {dataset.sampleImages.map((img: any, idx: number) => (
              <div key={idx} className="sample-image-wrapper">
                <img 
                  src={img.url} 
                  alt={`Sample ${idx + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                />
                <div className="sample-image-tooltip">{img.taskTitle}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <button
          onClick={handlePurchase}
          style={{
            padding: '12px 24px',
            background: colors.primary,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Purchase Access
        </button>
      </div>
    </div>
  );
}
